import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import Header from "../components/Header";
import ProgressBar from "../components/ProgressBar";
import PageTransition from "../components/PageTransition";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
import { User } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/store";
import { setVideoEmail } from "@/store/features/videoSlice";
import { useAuth } from "@/hooks/useAuth";

const Checkout = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const listing = useSelector((state: RootState) => state.listing);
  const video = useSelector((state: RootState) => state.video);
  const { user, isAuthenticated, session } = useAuth();

  // Create safe accessor functions for actor properties
  const getActorName = () => video.actor?.name || 'Selected Actor';
  const getActorAvatarUrl = () => {
    if (video.actor) {
      return video.actor.preview_image_url || video.actor.avatarUrl || '';
    }
    return '';
  };
  const getActorStyle = () => video.actor?.style || 'Standard';
  const getActorDescription = () => video.actor?.description || 'No description available';

  const email = useSelector((state: RootState) => state.video.email);

  // Prepopulate email field with the user's email if they are logged in
  useEffect(() => {
    if (isAuthenticated && user?.email && !email) {
      dispatch(setVideoEmail(user.email));
    }
  }, [isAuthenticated, user, dispatch, email]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsProcessing(true);

    try {
      // Call the process-order edge function
      const { data, error } = await supabase.functions.invoke('process-order', {
        body: {
          email,
          video,
          images: listing.images || [],
          listingUrl: video.listingUrl || ''
        }
      });

      if (error) {
        throw new Error(error.message || "Error processing order");
      }

      if (!data.success) {
        throw new Error(data.error || "Error processing order");
      }

      // Show success message
      toast({
        title: "Order Successful",
        description: "Your payment has been processed successfully.",
      });

      // Redirect to order confirmation with order info
      navigate(`/order-confirmation?email=${encodeURIComponent(email)}&order=${data.orderId}&newAccount=${data.isNewAccount}`);
    } catch (error) {
      console.error("Error in checkout process:", error);
      toast({
        title: "Error Processing Order",
        description: "There was a problem processing your order. Please try again.",
        variant: "destructive",
      });
      setIsProcessing(false);
    }
  };

  const handleEmailChange = (email: string) => {
    dispatch(setVideoEmail(email));
  };

  return <Layout>
      <PageTransition>
        <Header />
        <ProgressBar />

        <motion.div className="space-y-6" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2,
        duration: 0.5
      }}>
          <div className="text-center mb-6">
            <h2 className="font-bold tracking-tight mb-3 text-xl">Complete Your Order</h2>
            <p className="text-muted-foreground text-balance">
              Review your selection and complete payment to start creating your video.
            </p>
          </div>

          <div className="glass rounded-xl p-6 space-y-4">

            {/* Actor Card */}
            <div className="bg-secondary/30 rounded-lg p-4 mb-4 flex items-center">
              <div className="flex items-center space-x-4">
                <HoverCard>
                  <HoverCardTrigger asChild>
                    <div className="cursor-pointer">
                      <Avatar className="h-16 w-16 border-2 border-primary/20 shadow-sm">
                        <AvatarImage
                          src={getActorAvatarUrl()}
                          alt={getActorName()}
                        />
                        <AvatarFallback className="bg-primary/5">
                          <User className="h-6 w-6 text-primary/70" />
                        </AvatarFallback>
                      </Avatar>
                    </div>
                  </HoverCardTrigger>
                  <HoverCardContent className="w-80">
                    <div className="flex justify-between space-x-4">
                      <Avatar className="h-20 w-20">
                        <AvatarImage src={getActorAvatarUrl()} />
                        <AvatarFallback>{(getActorName()[0] || 'A')}</AvatarFallback>
                      </Avatar>
                      <div className="space-y-1">
                        <h4 className="text-sm font-semibold">{getActorName()}</h4>
                        <p className="text-sm text-muted-foreground">
                          {getActorDescription()}
                        </p>
                        <div className="flex items-center pt-2">
                          <span className="bg-primary/10 text-primary rounded-full px-2 py-0.5 text-xs font-medium">
                            {getActorStyle()}
                          </span>
                        </div>
                      </div>
                    </div>
                  </HoverCardContent>
                </HoverCard>
                <div>
                  <h3 className="font-medium">Your Actor</h3>
                  <p className="text-primary font-medium">{getActorName()}</p>
                  <p className="text-xs text-muted-foreground">Style: {getActorStyle()}</p>
                </div>
              </div>
            </div>

            <div className="bg-secondary/50 rounded-lg p-4 mb-3">
              <h3 className="font-medium mb-3">Order Summary</h3>

              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span>Professional Property Video Ad</span>
                  <span>$99.00</span>
                </div>
                <div className="flex justify-between text-sm text-muted-foreground">
                  <span>Selected Actor</span>
                  <span>{getActorName()}</span>
                </div>
                <div className="flex justify-between text-sm text-muted-foreground">
                  <span>Script Style</span>
                  <span>{getActorStyle()}</span>
                </div>
                <div className="border-t border-border pt-2 mt-2">
                  <div className="flex justify-between font-medium">
                    <span>Total</span>
                    <span>$99.00</span>
                  </div>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium mb-1">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  required
                  className="disabled:opacity-50 disabled:cursor-not-allowed w-full px-4 py-3 rounded-md border bg-background focus:outline-none input-focus"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => handleEmailChange(e.target.value)}
                  disabled={isAuthenticated}
                />
                {isAuthenticated && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Using email from your account
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="card" className="block text-sm font-medium mb-1">
                  Card Information
                </label>
                <div className="rounded-md border bg-background focus-within:ring-2 focus-within:ring-primary/10 transition-all">
                  <div className="px-4 py-3 flex items-center">
                    <input type="text" id="card" required className="w-full focus:outline-none bg-transparent" placeholder="Card number" />
                    <div className="flex space-x-2 ml-2">
                      <svg className="w-6 h-6 text-muted-foreground" viewBox="0 0 24 24" fill="none">
                        <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="2" />
                        <path d="M3 10H21" stroke="currentColor" strokeWidth="2" />
                      </svg>
                    </div>
                  </div>
                  <div className="border-t border-border">
                    <div className="grid grid-cols-2 gap-4 px-4 py-3">
                      <input type="text" className="focus:outline-none bg-transparent" placeholder="MM / YY" required />
                      <input type="text" className="focus:outline-none bg-transparent" placeholder="CVC" required />
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <label htmlFor="name" className="block text-sm font-medium mb-1">
                  Name on Card
                </label>
                <input type="text" id="name" required className="w-full px-4 py-3 rounded-md border bg-background focus:outline-none input-focus" placeholder="Card holder name" />
              </div>

              <div>
                <label htmlFor="company" className="block text-sm font-medium mb-1">
                  Company Name (Optional)
                </label>
                <input type="text" id="company" className="w-full px-4 py-3 rounded-md border bg-background focus:outline-none input-focus" placeholder="For business receipts" />
              </div>

              <div className="pt-2">
                <button type="submit" disabled={isProcessing} className="w-full bg-primary text-primary-foreground py-3 rounded-md font-medium flex items-center justify-center">
                  {isProcessing ? <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Processing...
                    </> : <>
                      Complete Order - $99.00
                    </>}
                </button>
              </div>

              <div className="flex items-center justify-center space-x-2 text-xs text-muted-foreground pt-2">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="m9 12 2 2 4-4" />
                </svg>
                <span>Secure payment processing</span>
              </div>
            </form>
          </div>

          <div className="glass rounded-xl p-6">
            <h3 className="font-medium mb-3">What happens after purchase?</h3>
            <ul className="space-y-3 text-sm">
              <li className="flex">
                <div className="mr-3 text-primary">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" />
                    <path d="m9 12 2 2 4-4" />
                  </svg>
                </div>
                <span>You'll receive an order confirmation email</span>
              </li>
              <li className="flex">
                <div className="mr-3 text-primary">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" />
                    <path d="m9 12 2 2 4-4" />
                  </svg>
                </div>
                <span>Your video will enter production immediately</span>
              </li>
              <li className="flex">
                <div className="mr-3 text-primary">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" />
                    <path d="m9 12 2 2 4-4" />
                  </svg>
                </div>
                <span>Track progress in your dashboard (typically delivered in 48 hours)</span>
              </li>
              <li className="flex">
                <div className="mr-3 text-primary">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" />
                    <path d="m9 12 2 2 4-4" />
                  </svg>
                </div>
                <span>Download and share your completed video</span>
              </li>
            </ul>
          </div>
        </motion.div>
      </PageTransition>
    </Layout>;
};

export default Checkout;
